import { useState } from 'react';
import * as Avatar from '@givebutter/primitives/avatar';
import { Button } from '@givebutter/primitives/button';
import * as Combobox from '@givebutter/primitives/combobox';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AssigneeComboboxItems } from 'components/tasks/AssigneeComboboxItems';
import { useCrmMembers } from 'components/tasks/utils/useCrmMembers';
import { useUpdateTask } from 'components/tasks/utils/useUpdateTask';
import { getAvatarFallbackColor } from 'components/util/getAvatarFallbackColor';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { getInitialsFromName } from 'helpers/formatting';
import { Task } from 'types/domain';

const AssignTaskAction = ({ task }: { task: Task }) => {
    const [inputValue, setInputValue] = useState('');
    const searchTerm = useDebounce(inputValue);

    const [isPending, params] = useIsPending(
        {
            query: searchTerm || undefined,
            limit: 9999,
        },
        500,
    );

    const { data: members } = useCrmMembers(params);
    const assignedToMember = members.find(m => m.id === task.assigned_to);

    const { mutate: updateTask } = useUpdateTask();

    return (
        <Combobox.Root
            resource="assignee"
            allowMultiple={false}
            defaultValue={assignedToMember ? [assignedToMember.id.toString()] : []}
            onChangeValue={value => {
                updateTask({
                    taskId: task.id,
                    params: {
                        assigned_to: parseInt(value[0]),
                    },
                });
            }}
        >
            <Combobox.Trigger>
                <Button
                    variant="none"
                    onClick={e => e.stopPropagation()}
                    className="group/avatar z-layerFour relative flex"
                >
                    {assignedToMember ? (
                        <Avatar.Root
                            size="2xs"
                            className="ring-gray-400 ring-offset-0 group-hover/avatar:ring-2"
                        >
                            <Avatar.Fallback
                                className={getAvatarFallbackColor(assignedToMember.first_name)}
                                aria-label={`Avatar for ${assignedToMember.first_name} ${assignedToMember.last_name}`}
                            >
                                {getInitialsFromName(
                                    assignedToMember.first_name,
                                    assignedToMember.last_name,
                                )}
                            </Avatar.Fallback>
                        </Avatar.Root>
                    ) : (
                        <span className="flex h-5 w-5 border-spacing-2 items-center justify-center rounded-full border border-dashed border-gray-400 group-hover/avatar:border-gray-600">
                            <FontAwesomeIcon
                                icon={faUser}
                                size="xs"
                                className="text-gray-400 group-hover/avatar:text-gray-600"
                            />
                        </span>
                    )}
                </Button>
            </Combobox.Trigger>

            <Combobox.Popover align="end" className="w-80">
                <Combobox.Search
                    value={inputValue}
                    onChangeValue={setInputValue}
                    placeholder="Search assignee"
                />
                <AssigneeComboboxItems isLoading={isPending} members={members} />
            </Combobox.Popover>
        </Combobox.Root>
    );
};

export { AssignTaskAction };
